import React, { useState, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTranslations } from 'react-ui';
import { FormFooter } from '../common/FormFooter';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

interface AiPosterEntryProps {
  isExtracting: boolean;
  extractionError: string | null;
  onExtract: (file: File) => void;
  onClearError: () => void;
  onCancel: () => void;
  onFileSelected?: (file: File) => void;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/**
 * AI poster entry panel: image dropzone / camera capture + extract button.
 * On native (Capacitor), tapping opens the camera/gallery prompt.
 * On web, falls back to browser file input.
 * Used in the 'ai-poster' entry mode of QuickCreateModal.
 */
export const AiPosterEntry: React.FC<AiPosterEntryProps> = ({
  isExtracting,
  extractionError,
  onExtract,
  onClearError,
  onCancel,
  onFileSelected,
}) => {
  const { translated } = useTranslations();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback((file: File) => {
    setFileError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError(translated('sc_ai_poster_invalid_type'));
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setFileError(translated('sc_ai_poster_too_large').replace('{mb}', String(MAX_SIZE_MB)));
      return;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    if (extractionError) onClearError();
    onFileSelected?.(file);
  }, [translated, extractionError, onClearError]);

  const handleOpen = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        // Skip permission check - Camera.getPhoto handles permissions internally on Android
        const photo = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt, // Let user choose camera or gallery
        });
        if (photo.dataUrl) {
          const response = await fetch(photo.dataUrl);
          const blob = await response.blob();
          const mimeMatch = /^data:([^;]+)/.exec(photo.dataUrl);
          const mimeType = mimeMatch?.[1] ?? 'image/jpeg';
          const ext = photo.format ?? 'jpg';
          const file = new File([blob], `photo.${ext}`, { type: mimeType });
          processFile(file);
        }
      } catch (error: unknown) {
        // User cancelled or permission denied — only log, don't display error
        const msg = error instanceof Error ? error.message : String(error);
        if (!msg.includes('cancel') && !msg.includes('Cancel')) {
          console.error('Camera error:', error);
        }
      }
    } else {
      inputRef.current?.click();
    }
  }, [processFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleClearFile = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = '';
    onClearError();
  };

  const handleExtract = () => {
    if (selectedFile && !isExtracting) {
      onExtract(selectedFile);
    }
  };

  const canSubmit = Boolean(selectedFile) && !isExtracting;
  const displayError = fileError ?? extractionError;

  return (
    <div className="w-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4 pb-2">
      <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-medium text-amber-700">
        <span>🧪</span>
        <span>{translated('sc_ai_beta_note')}</span>
      </div>

      {!selectedFile ? (
        <div
          role="button"
          tabIndex={0}
          aria-label={translated('sc_ai_poster_dropzone')}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleOpen}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && void handleOpen()}
          className={`
            flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed
            cursor-pointer transition-colors min-h-[240px]
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
            ${isDragOver
              ? 'border-primary-400 bg-primary-50 text-primary-700'
              : 'border-gray-300 bg-gray-50 hover:border-primary-300 hover:bg-primary-50/50 text-gray-500'}
          `}
        >
          <span className="text-4xl">📷</span>
          <div className="text-center px-4">
            <p className="text-sm font-medium text-gray-700">{translated('sc_ai_poster_dropzone')}</p>
            <p className="text-xs text-gray-500 mt-1">
              JPG, PNG, WEBP · max {MAX_SIZE_MB}MB
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="relative rounded-xl overflow-hidden bg-gray-100 min-h-[200px] flex items-center justify-center">
            <img
              src={previewUrl ?? ''}
              alt="Selected poster"
              className="max-h-64 max-w-full object-contain"
            />
            <button
              type="button"
              onClick={handleClearFile}
              disabled={isExtracting}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 text-white text-sm
                hover:bg-black/80 transition-colors disabled:opacity-50"
              aria-label="Remove image"
            >
              ✕
            </button>
          </div>
          <p className="text-xs text-gray-500 truncate">{selectedFile.name}</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileChange}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
      />

      <p className="text-sm text-gray-700">
        <strong>{translated('sc_ai_poster_hint')}</strong>
      </p>

      {displayError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800"
        >
          <span className="shrink-0 mt-0.5">❌</span>
          <span className="flex-1">{displayError}</span>
          <button
            type="button"
            onClick={() => { setFileError(null); onClearError(); }}
            className="shrink-0 p-0.5 hover:opacity-70 transition-opacity"
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}
      </div>

      <FormFooter
        onCancel={onCancel}
        onContinue={handleExtract}
        cancelLabel={translated('cancel')}
        continueLabel={`${translated('sc_ai_poster_extract')} →`}
        cancelDisabled={isExtracting}
        continueDisabled={!canSubmit}
        isLoading={isExtracting}
        loadingLabel={translated('sc_ai_poster_extracting')}
      />
    </div>
  );
};
